import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, CircularProgress, Container, Link, Typography, Collapse } from "@mui/material";
import ApiInstance from "../api/ApiInstance";
import { useAuth } from "../context/AuthContext";
import AuthPanel from "./AuthPanel";
import Logo from "./Logo";

/**
 * Pre-launch gate: while the platform runs in private mode, visitors only see
 * a "coming soon" screen; the full app renders exclusively for a logged-in
 * platform_admin. Fail closed: until /public-config answers (or if it errors),
 * we show a spinner/gate — never the real content.
 */
export default function PrivateGate({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { user, loading: authLoading, logout } = useAuth();
  // null = still loading; true/false = resolved.
  const [privateMode, setPrivateMode] = useState<boolean | null>(null);
  const [showLogin, setShowLogin] = useState(false);

  useEffect(() => {
    ApiInstance.getApi()
      .get("/public-config", { params: { t: Date.now() } }) // cache-bust: reload direct na admin-toggle mag geen 30s-oude waarde zien
      .then(({ data }) => setPrivateMode(data?.privateMode !== false))
      .catch(() => setPrivateMode(true)); // fail closed
  }, []);

  const gated = privateMode !== false && !(privateMode === true && !authLoading && user?.role === "platform_admin");

  // Keep the gate screen out of search indexes while it is shown.
  useEffect(() => {
    if (!gated) return;
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, [gated]);

  // Site is open: behave exactly as before.
  if (privateMode === false) return <>{children}</>;

  // No flash of content (or of the gate, for a returning admin) while loading.
  if (privateMode === null || authLoading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress aria-label={t("gate.loading")} />
      </Box>
    );
  }

  // Private mode: only a platform admin gets through.
  if (user?.role === "platform_admin") return <>{children}</>;

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "var(--bg, #faf7f2)" }}>
      <Container maxWidth="xs" sx={{ py: 6, textAlign: "center" }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Logo height={44} />
        </Box>
        <Typography variant="h1" sx={{ fontSize: "1.8rem", mb: 1 }}>{t("gate.title")}</Typography>
        <Typography sx={{ color: "text.secondary", mb: 4 }}>{t("gate.body")}</Typography>

        {user ? (
          // Logged in, but not a platform admin: stay gated.
          <>
            <Typography sx={{ color: "text.secondary", fontSize: ".9rem", mb: 2 }}>{t("gate.noAccess")}</Typography>
            <Button variant="outlined" size="small" onClick={() => logout()}>{t("gate.logout")}</Button>
          </>
        ) : (
          <>
            <Collapse in={showLogin}>
              <Box sx={{ textAlign: "left", mb: 2 }}>
                <AuthPanel initialMode="login" />
              </Box>
            </Collapse>
            {!showLogin && (
              <Link component="button" type="button" onClick={() => setShowLogin(true)}
                sx={{ fontSize: ".85rem", color: "text.secondary" }}>
                {t("gate.adminLogin")}
              </Link>
            )}
          </>
        )}
      </Container>
    </Box>
  );
}
